import { useCallback, useEffect, useRef, useState } from 'react';
import { AIIntent } from '../api/ai.api';
import { ChatMessage } from '../components/Modal/AITaskDescription/components/AIDescriptionChat';
import { getTaskTypeInstruction } from '../components/Modal/AITaskDescription/constants/taskTypeInstructions';
import { AITaskSuggestionOptions } from '../types/AI';
import { useAITaskSuggestion } from './useAITaskSuggestion';

interface UseAIDescriptionChatOptions {
  workspaceId: number;
  taskTitle: string;
  taskType: string;
  initialDescription: string;
  onDescriptionChange: (description: string) => void;
}

interface UseAIDescriptionChatReturn {
  messages: ChatMessage[];
  userInput: string;
  setUserInput: (value: string) => void;
  sendMessage: (messageText?: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  description: string;
  setDescription: (value: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  selectedContext: string;
  setSelectedContext: (value: string) => void;
  clearSelectedContext: () => void;
  currentIntent: AIIntent | null;
  intentReasoning: string | null;
  resetChat: () => void;
}

export const useAIDescriptionChat = ({
  workspaceId,
  taskTitle,
  taskType,
  initialDescription,
  onDescriptionChange,
}: UseAIDescriptionChatOptions): UseAIDescriptionChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [userInput, setUserInput] = useState('');
  const [description, setDescriptionState] = useState(initialDescription);
  const [selectedContext, setSelectedContext] = useState('');
  const [lastAIResponse, setLastAIResponse] = useState('');

  // Undo/Redo history
  const [history, setHistory] = useState<string[]>([initialDescription]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Flag to prevent editor onChange from adding to history during programmatic updates (undo/redo/AI)
  const isProgrammaticUpdateRef = useRef(false);

  const messageIdCounter = useRef(0);
  const currentDescriptionRef = useRef(initialDescription);
  const hasInitializedDescriptionRef = useRef(false);

  // Refs to avoid stale closures in undo/redo
  const historyRef = useRef<string[]>([initialDescription]);
  const historyIndexRef = useRef(0);

  // Keep refs in sync with state for undo/redo
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  // Update description when initialDescription changes (e.g., when task loads)
  useEffect(() => {
    if (initialDescription && !hasInitializedDescriptionRef.current && description === '') {
      setDescriptionState(initialDescription);
      currentDescriptionRef.current = initialDescription;
      hasInitializedDescriptionRef.current = true;
    }
  }, [initialDescription, description]);

  // Keep ref in sync with description state
  useEffect(() => {
    currentDescriptionRef.current = description;
  }, [description]);

  // Initialize with greeting message
  useEffect(() => {
    const instruction = getTaskTypeInstruction(taskType, !!initialDescription);
    const greetingMessage: ChatMessage = {
      id: `msg-${messageIdCounter.current++}`,
      role: 'assistant',
      content: formatInstructionMessage(instruction.greeting, instruction.questions),
      timestamp: new Date(),
    };
    setMessages([greetingMessage]);
  }, [taskType, initialDescription]);

  const formatInstructionMessage = (greeting: string, questions: string[]): string => {
    let html = greeting
      .split('\n')
      .map((line) => `<p>${line}</p>`)
      .join('');

    if (questions.length > 0) {
      html += '<ol>';
      questions.forEach((question) => {
        html += `<li><strong>${question}</strong></li>`;
      });
      html += '</ol>';
    }

    return html;
  };

  // Helper to strip HTML and normalize text for comparison
  // Remove ALL whitespace to catch Tiptap reformatting variations
  const normalizeContent = (html: string): string => {
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, '')
      .toLowerCase();
  };

  const addToHistory = useCallback((newDescription: string) => {
    const normalizedNew = normalizeContent(newDescription);

    setHistory((prev) => {
      // Check if the last entry in current history is the same
      // This catches duplicates from rapid onChange events
      const currentLastEntry = prev[prev.length - 1];
      const normalizedLast = currentLastEntry ? normalizeContent(currentLastEntry) : '';

      if (currentLastEntry && normalizedLast === normalizedNew) {
        return prev; // No change - it's a duplicate
      }

      // Use ref to get current index to avoid stale closure
      const currentIndex = historyIndexRef.current;
      const newHistory = prev.slice(0, currentIndex + 1);

      newHistory.push(newDescription);
      return newHistory;
    });

    // Increment index - check against actual state to see if we should
    setHistoryIndex((prevIndex) => {
      const currentHistory = historyRef.current;
      const lastEntry = currentHistory[currentHistory.length - 1];

      // Only increment if the last entry is different from what we're adding
      if (lastEntry && normalizeContent(lastEntry) === normalizedNew) {
        return prevIndex; // Don't increment - duplicate was caught
      }
      return prevIndex + 1;
    });
  }, []);

  const setDescription = useCallback(
    (value: string) => {
      setDescriptionState(value);

      // Only add to history if not a programmatic update (undo/redo/AI)
      // Duplicates are handled by addToHistory itself
      if (!isProgrammaticUpdateRef.current) {
        addToHistory(value);
      }

      onDescriptionChange(value);
    },
    [addToHistory, onDescriptionChange]
  );

  const undo = useCallback(() => {
    const currentIndex = historyIndexRef.current;
    const currentHistory = historyRef.current;
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setHistoryIndex(newIndex);
      const previousDescription = currentHistory[newIndex];

      // Set flag to prevent adding to history when editor updates
      isProgrammaticUpdateRef.current = true;
      setDescriptionState(previousDescription);
      onDescriptionChange(previousDescription);
      // Reset flag after a brief delay to allow editor to process the update
      setTimeout(() => {
        isProgrammaticUpdateRef.current = false;
      }, 50);
    }
  }, [onDescriptionChange]);

  const redo = useCallback(() => {
    const currentIndex = historyIndexRef.current;
    const currentHistory = historyRef.current;
    if (currentIndex < currentHistory.length - 1) {
      const newIndex = currentIndex + 1;
      setHistoryIndex(newIndex);
      const nextDescription = currentHistory[newIndex];

      // Set flag to prevent adding to history when editor updates
      isProgrammaticUpdateRef.current = true;
      setDescriptionState(nextDescription);
      onDescriptionChange(nextDescription);
      // Reset flag after a brief delay to allow editor to process the update
      setTimeout(() => {
        isProgrammaticUpdateRef.current = false;
      }, 50);
    }
  }, [onDescriptionChange]);

  const clearSelectedContext = useCallback(() => {
    setSelectedContext('');
  }, []);

  const resetChat = useCallback(() => {
    // Reset chat state with dynamic greeting message
    const instruction = getTaskTypeInstruction(taskType, !!initialDescription);
    const greetingMessage: ChatMessage = {
      id: `msg-${messageIdCounter.current++}`,
      role: 'assistant',
      content: formatInstructionMessage(instruction.greeting, instruction.questions),
      timestamp: new Date(),
    };
    setMessages([greetingMessage]);
    setUserInput('');
    setSelectedContext('');

    // Reset description to initial value (programmatic update to prevent adding to history)
    isProgrammaticUpdateRef.current = true;
    setDescriptionState(initialDescription);

    // Reset history
    setHistory([initialDescription]);
    setHistoryIndex(0);
    // Reset refs
    historyRef.current = [initialDescription];
    historyIndexRef.current = 0;
    currentDescriptionRef.current = initialDescription;
    setLastAIResponse('');

    // Reset flag after a brief delay
    setTimeout(() => {
      isProgrammaticUpdateRef.current = false;
    }, 100);
  }, [initialDescription, taskType]);

  const streamedDescriptionRef = useRef('');
  const streamedChatRef = useRef('');
  const currentIntentRef = useRef<AIIntent | null>(null);
  const isFirstDescriptionChunkRef = useRef(true);
  const streamingChatMessageIdRef = useRef<string | null>(null);

  const aiSuggestion = useAITaskSuggestion({
    workspaceId,
    onIntent: (intent: AIIntent, reasoning: string) => {
      // Store the detected intent
      currentIntentRef.current = intent;

      // Create initial streaming message for chat responses
      if (intent === 'chat' || intent === 'both') {
        const streamingMsgId = `msg-${messageIdCounter.current++}`;
        streamingChatMessageIdRef.current = streamingMsgId;
        const streamingMsg: ChatMessage = {
          id: streamingMsgId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, streamingMsg]);
        streamedChatRef.current = '';
      }
    },
    onChatChunk: (chunk: string) => {
      // Accumulate chat chunks
      streamedChatRef.current += chunk;

      // Update the streaming message in real-time
      if (streamingChatMessageIdRef.current) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === streamingChatMessageIdRef.current ? { ...msg, content: streamedChatRef.current } : msg
          )
        );
      }

      // Store the accumulated chat response for context
      setLastAIResponse(streamedChatRef.current);
    },
    onDescriptionChunk: (chunk: string) => {
      // On first chunk, clear the description to start fresh
      if (isFirstDescriptionChunkRef.current) {
        streamedDescriptionRef.current = '';
        isFirstDescriptionChunkRef.current = false;
      }

      // Stream chunk directly to description editor
      streamedDescriptionRef.current += chunk;
      // Use the accumulated streamed value directly to avoid stale state issues
      const newValue = streamedDescriptionRef.current;
      setDescriptionState(newValue);
      currentDescriptionRef.current = newValue;
    },
    onChatDone: () => {
      // Chat phase complete (only in 'both' mode) - finalize the streaming message
      streamingChatMessageIdRef.current = null;

      // Add transition message
      const chatPhaseMessage: ChatMessage = {
        id: `msg-${messageIdCounter.current++}`,
        role: 'assistant',
        content: '<p><em>Now updating the description...</em></p>',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, chatPhaseMessage]);
    },
    onSuccess: (response) => {
      const intent = response.intent || currentIntentRef.current;

      // If we had a streaming chat message, finalize it (in case not done by onChatDone)
      // This handles the 'chat' intent where onChatDone is not called
      streamingChatMessageIdRef.current = null;

      // Update description history if description was modified
      if (streamedDescriptionRef.current) {
        // Set flag BEFORE adding to history to catch all Tiptap onChange events
        isProgrammaticUpdateRef.current = true;

        addToHistory(streamedDescriptionRef.current);
        onDescriptionChange(streamedDescriptionRef.current);

        // Keep flag set longer to catch all Tiptap reformatting onChange events
        setTimeout(() => {
          isProgrammaticUpdateRef.current = false;
        }, 2000);

        // Add acknowledgment if description was updated
        if (intent === 'update' || intent === 'both') {
          const ackMessage: ChatMessage = {
            id: `msg-${messageIdCounter.current++}`,
            role: 'assistant',
            content:
              "<p>I've updated the task description based on your input. Please review it and let me know if you'd like me to make any adjustments.</p>",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, ackMessage]);

          // Clear previous AI response after description update
          setLastAIResponse('');
        }
      }

      // Reset refs for next generation (description ref is reset on first chunk)
      streamedChatRef.current = '';
      currentIntentRef.current = null;
    },
    onError: (error) => {
      // If there's a streaming message, remove it
      if (streamingChatMessageIdRef.current) {
        const streamingId = streamingChatMessageIdRef.current;
        setMessages((prev) => prev.filter((msg) => msg.id !== streamingId));
        streamingChatMessageIdRef.current = null;
      }

      // Add error message to chat
      const errorMessage: ChatMessage = {
        id: `msg-${messageIdCounter.current++}`,
        role: 'assistant',
        content: `<p style="color: red;">Error: ${error}</p>`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);

      // Reset refs on error (description ref is reset on first chunk)
      streamedChatRef.current = '';
      currentIntentRef.current = null;
    },
  });

  const sendMessage = useCallback(
    async (messageText?: string) => {
      const textToSend = messageText || userInput;
      if (!textToSend.trim() || aiSuggestion.loading) return;

      // Add user message to chat with selected context as separate field
      const userMessage: ChatMessage = {
        id: `msg-${messageIdCounter.current++}`,
        role: 'user',
        content: `<p>${textToSend}</p>`,
        timestamp: new Date(),
        contextText: selectedContext && selectedContext.trim() ? selectedContext : undefined,
      };

      setMessages((prev) => [...prev, userMessage]);
      const currentUserInput = textToSend;
      const currentSelectedContext = selectedContext;

      // Only clear input if we're using the state value (not when messageText is provided)
      if (!messageText) {
        setUserInput('');
      }
      clearSelectedContext(); // Clear selected context after capturing it

      // Use ref to get current description (avoids stale closure)
      const currentDescription = currentDescriptionRef.current;

      // Reset the first chunk flag for the new generation
      isFirstDescriptionChunkRef.current = true;

      // Generate AI suggestion with user's message and selected text as context
      const feedbackWithContext = currentSelectedContext
        ? `Selected text: "${currentSelectedContext}"\n\nUser feedback: ${currentUserInput}`
        : currentUserInput;

      const options: AITaskSuggestionOptions = {
        detailLevel: 'detailed',
        includeAcceptanceCriteria: true,
        includeTechnicalNotes: false,
        format: 'html',
        userFeedback: feedbackWithContext,
        // Include last AI response if available (up to 1000 chars for context)
        previousAIResponse: lastAIResponse ? lastAIResponse.substring(0, 1000) : undefined,
      };

      await aiSuggestion.generateSuggestion(
        taskTitle,
        taskType,
        currentDescription, // Pass the stored description for intent classification
        options
      );
    },
    [userInput, aiSuggestion, taskTitle, taskType, selectedContext, clearSelectedContext, lastAIResponse]
  );

  return {
    messages,
    userInput,
    setUserInput,
    sendMessage,
    isLoading: aiSuggestion.loading,
    error: aiSuggestion.error,
    description,
    setDescription,
    undo,
    redo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    selectedContext,
    setSelectedContext,
    clearSelectedContext,
    currentIntent: aiSuggestion.intent,
    intentReasoning: aiSuggestion.intentReasoning,
    resetChat,
  };
};
